import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Award, Star, Home, Scissors, Clock, Calendar, ArrowRight, 
  Phone, MapPin, Sparkles, Crown, Heart, Users
} from 'lucide-react';
import { SkeletonGrid } from '../components/ui/Skeleton';
import { fetchServices } from '../services/api';
import type { ServiceType } from '../types';

const HomePage = () => {
  const [heroImage, setHeroImage] = useState('/img/1.jpg');
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [services, setServices] = useState<ServiceType[]>([]);
  const [loadingServices, setLoadingServices] = useState(true);
  const [imagesLoaded, setImagesLoaded] = useState<Record<string, boolean>>({});

  useEffect(() => {
    // Randomly select a hero image
    const imageNumber = Math.floor(Math.random() * 5) + 1;
    setHeroImage(`/img/${imageNumber}.jpg`);
    
    // Load services for styles showcase
    loadServices();
  }, []);
  
  const loadServices = async () => {
    try {
      setLoadingServices(true);
      const data = await fetchServices();
      setServices(data.slice(0, 6)); // Show first 6 services
    } catch (err) {
      console.error('Failed to load services:', err);
    } finally {
      setLoadingServices(false);
    }
  };
  
  const handleImageLoad = useCallback((id: string) => {
    setImagesLoaded(prev => ({ ...prev, [id]: true }));
  }, []);

  const testimonials = [
    {
      name: "Sarah Johnson",
      text: "Absolutely amazing service! My braids lasted for weeks and looked perfect. The attention to detail is incredible.",
      rating: 5,
      image: "/img/avatar-placeholder.svg"
    },
    {
      name: "Maya Patel",
      text: "Professional, friendly, and skilled. I've been coming here for over a year and always leave satisfied.",
      rating: 5,
      image: "/img/avatar-placeholder.svg"
    },
    {
      name: "Zara Williams",
      text: "The best braiding service in Plymouth! Clean environment, fair prices, and beautiful results every time.",
      rating: 5,
      image: "/img/avatar-placeholder.svg"
    }
  ];

  // Auto-rotate testimonials
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  const features = [
    {
      title: "Expert Stylists",
      description: "Our team of professional stylists brings years of experience and expertise in creating beautiful, long-lasting protective styles.",
      icon: <Award className="w-12 h-12 text-primary-600" />,
      gradient: "from-blue-500 to-purple-600"
    },
    {
      title: "Quality Products",
      description: "We use only premium hair care products and materials to ensure the best results and protect your natural hair.",
      icon: <Star className="w-12 h-12 text-primary-600" />,
      gradient: "from-purple-500 to-pink-600"
    },
    {
      title: "Comfortable Environment",
      description: "Enjoy our relaxing, clean, and modern home salon environment designed for your comfort and satisfaction.",
      icon: <Home className="w-12 h-12 text-primary-600" />,
      gradient: "from-pink-500 to-red-600"
    },
    {
      title: "Happy Clients",
      description: "Join hundreds of satisfied customers who trust us with their hair care needs and styling requirements.",
      icon: <Users className="w-12 h-12 text-primary-600" />,
      gradient: "from-green-500 to-teal-600"
    }
  ];

  const stats = [
    { number: "500+", label: "Happy Clients", icon: <Heart className="w-6 h-6" /> },
    { number: "50+", label: "Style Options", icon: <Scissors className="w-6 h-6" /> },
    { number: "10+", label: "Years Experience", icon: <Award className="w-6 h-6" /> },
    { number: "100%", label: "Satisfaction", icon: <Star className="w-6 h-6" /> }
  ];

  return (
    <div className="flex flex-col min-h-screen">
      {/* Hero Section */}
      <motion.section 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
        className="relative h-screen flex items-center justify-center overflow-hidden"
      >
        <motion.div 
          initial={{ scale: 1.1 }}
          animate={{ scale: 1 }}
          transition={{ duration: 10, ease: "easeOut" }}
          className="absolute inset-0"
        >
          <img 
            src={heroImage}
            alt="Hero Background" 
            className="w-full h-full object-cover"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.src = '/img/1.jpg'; // fallback to first image
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/70"></div>
        </motion.div>
        
        {/* Floating elements */}
        <motion.div
          animate={{ 
            y: [0, -20, 0],
            rotate: [0, 5, 0]
          }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-20 left-10 text-primary-400 opacity-20"
        >
          <Sparkles className="w-16 h-16" />
        </motion.div>
        
        <motion.div
          animate={{ 
            y: [0, 20, 0],
            rotate: [0, -5, 0]
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          className="absolute bottom-20 right-10 text-secondary-400 opacity-20"
        >
          <Crown className="w-20 h-20" />
        </motion.div>

        <div className="container-custom relative z-10 text-center text-white">
          <motion.div 
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.8 }}
            className="max-w-4xl mx-auto px-4"
          >
            <motion.h1 
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.7, duration: 0.8 }}
              className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-white via-primary-200 to-secondary-200 bg-clip-text text-transparent"
            >
              Beautiful Braids,
              <br />
              <span className="text-primary-400">Exceptional Service</span>
            </motion.h1>
            
            <motion.p 
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.9, duration: 0.8 }}
              className="text-xl md:text-2xl mb-8 max-w-2xl mx-auto text-gray-200"
            >
              Professional hair braiding services in Plymouth. Specializing in boho braids, knotless braids, box braids, stitch braids, and more.
            </motion.p>
            
            <motion.div 
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 1.1, duration: 0.8 }}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link 
                  to="/booking" 
                  className="inline-flex items-center bg-gradient-to-r from-primary-600 to-secondary-600 text-white px-8 py-4 rounded-full text-lg font-semibold hover:from-primary-700 hover:to-secondary-700 transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  Book Appointment
                </Link>
              </motion.div>
              
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link 
                  to="/gallery" 
                  className="inline-flex items-center border-2 border-white text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-white hover:text-gray-900 transition-all duration-300"
                >
                  <Sparkles className="w-5 h-5 mr-2" />
                  Explore Styles
                </Link>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-white/60"
        >
          <div className="w-6 h-10 border-2 border-white/60 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white/60 rounded-full mt-2"></div>
          </div>
        </motion.div>
      </motion.section>

      {/* Stats Section */}
      <motion.section 
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
        className="py-16 bg-gradient-to-r from-primary-600 to-secondary-600 text-white"
      >
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ scale: 0.8, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="flex justify-center mb-3 text-white/80">
                  {stat.icon}
                </div>
                <div className="text-3xl md:text-4xl font-bold mb-2">{stat.number}</div>
                <div className="text-white/80">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Popular Styles Showcase */}
      <motion.section 
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
        className="py-20 bg-gradient-to-br from-gray-50 via-white to-primary-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 overflow-hidden"
      >
        <div className="container-custom">
          <motion.div 
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-full text-sm font-medium mb-4"
            >
              <Sparkles className="w-4 h-4" />
              Featured Styles
            </motion.div>
            <h2 className="text-3xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
              Popular Braiding Styles
            </h2>
            <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              Discover our most requested styles. Each one crafted with precision and care to bring out your natural beauty.
            </p>
          </motion.div>

          {loadingServices ? (
            <SkeletonGrid count={6} columns={3} />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <AnimatePresence>
                {services.map((service, index) => (
                  <motion.div
                    key={service.id}
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ 
                      delay: index * 0.1, 
                      duration: 0.5,
                      type: "spring",
                      stiffness: 100
                    }}
                    viewport={{ once: true }}
                    whileHover={{ y: -10, scale: 1.02 }}
                    className="group relative bg-white dark:bg-gray-800 rounded-2xl shadow-lg hover:shadow-2xl overflow-hidden cursor-pointer"
                    onClick={() => window.location.href = `/booking?service=${service.id}`}
                  >
                    {/* Image Container with Skeleton */}
                    <div className="relative h-64 overflow-hidden">
                      {!imagesLoaded[`service-${service.id}`] && (
                        <div className="absolute inset-0 skeleton-shimmer bg-gray-200 dark:bg-gray-700" />
                      )}
                      <motion.img
                        src={service.image || '/img/placeholder.svg'}
                        alt={service.name}
                        className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-110 ${
                          imagesLoaded[`service-${service.id}`] ? 'opacity-100' : 'opacity-0'
                        }`}
                        onLoad={() => handleImageLoad(`service-${service.id}`)}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = '/img/placeholder.svg';
                          handleImageLoad(`service-${service.id}`);
                        }}
                      />
                      {/* Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      
                      {/* Price Badge */}
                      <motion.div 
                        initial={{ x: 20, opacity: 0 }}
                        whileInView={{ x: 0, opacity: 1 }}
                        transition={{ delay: index * 0.1 + 0.3 }}
                        className="absolute top-4 right-4 bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm px-3 py-1 rounded-full shadow-lg"
                      >
                        <span className="font-bold text-primary-600">£{service.price}</span>
                      </motion.div>
                      
                      {/* Hover Action */}
                      <motion.div 
                        initial={{ y: 20, opacity: 0 }}
                        whileHover={{ y: 0, opacity: 1 }}
                        className="absolute bottom-4 left-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-300"
                      >
                        <button className="w-full bg-white text-gray-900 py-2 rounded-lg font-medium hover:bg-primary-50 transition-colors flex items-center justify-center gap-2">
                          <Calendar className="w-4 h-4" />
                          Book Now
                        </button>
                      </motion.div>
                    </div>
                    
                    {/* Content */}
                    <div className="p-6">
                      <h3 className="text-xl font-bold mb-2 group-hover:text-primary-600 transition-colors">
                        {service.name}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400 text-sm mb-4 line-clamp-2">
                        {service.description}
                      </p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <Clock className="w-4 h-4" />
                          <span>{service.duration}</span>
                        </div>
                        <motion.div
                          whileHover={{ x: 5 }}
                          className="text-primary-600 flex items-center gap-1 text-sm font-medium"
                        >
                          View Details
                          <ArrowRight className="w-4 h-4" />
                        </motion.div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          )}

          <motion.div 
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Link
              to="/gallery"
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-primary-600 to-secondary-600 text-white rounded-full font-semibold hover:shadow-lg hover:scale-105 transition-all duration-300"
            >
              <Sparkles className="w-5 h-5" />
              View All Styles
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </motion.section>

      {/* Meet the Stylist Section */}
      <motion.section 
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
        className="py-20 bg-white dark:bg-gray-900"
      >
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div 
              initial={{ x: -50, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="relative overflow-hidden rounded-2xl shadow-2xl">
                <img 
                  src="/plymouth.jpg"
                  alt="Rasheeda - Plymouth Braider"
                  className="w-full max-w-md mx-auto object-cover"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = '/img/2.jpg'; // fallback image
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
              </div>
              <motion.div 
                initial={{ scale: 0.8, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.3, duration: 0.5 }}
                viewport={{ once: true }}
                className="absolute -bottom-6 -right-6 bg-gradient-to-r from-primary-600 to-secondary-600 text-white p-6 rounded-2xl shadow-xl max-w-xs"
              >
                <Crown className="w-8 h-8 mb-2 text-yellow-300" />
                <p className="text-lg font-semibold">10+ Years Experience</p>
                <p className="text-sm opacity-90">Creating beautiful braids and happy clients</p>
              </motion.div>
            </motion.div>
            
            <motion.div
              initial={{ x: 50, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
                Meet Your Stylist
              </h2>
              <p className="text-xl font-medium text-primary-600 mb-4">
                Hey, I'm Rasheeda (but everyone calls me Sheedah! 😊)
              </p>
              <div className="space-y-4 text-gray-600 dark:text-gray-400">
                <p>
                  I've been transforming hair and creating smiles for over a decade now. My journey into braiding started as a passion and turned into an art form I absolutely love sharing with my clients.
                </p>
                <p>
                  What makes me different? Well, besides my slightly obsessive attention to detail (your edges will thank me later!), I believe in creating a relaxed, friendly atmosphere where you can unwind while I work my magic. And yes, I do consider myself a hair magician! ✨
                </p>
                <p>
                  Whether you're looking for classic box braids, trendy knotless styles, or want to try something completely unique, I'm here to bring your vision to life while keeping your natural hair healthy and protected.
                </p>
                <motion.div whileHover={{ x: 5 }} className="pt-4">
                  <Link 
                    to="/booking" 
                    className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium text-lg group"
                  >
                    Book Your Session 
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* Features Section */}
      <motion.section 
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
        className="py-20 bg-gradient-to-br from-gray-50 to-primary-50 dark:from-gray-800 dark:to-gray-900"
      >
        <div className="container-custom">
          <motion.div 
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Why Choose Us</h2>
            <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              At Plymouth Braider, we're committed to providing exceptional service and creating beautiful, long-lasting styles that enhance your natural beauty.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div 
                key={index}
                initial={{ y: 50, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                viewport={{ once: true }}
                whileHover={{ y: -10, scale: 1.02 }}
                className="text-center p-8 bg-white dark:bg-gray-800 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 group"
              >
                <motion.div 
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  className="flex justify-center mb-4"
                >
                  {feature.icon}
                </motion.div>
                <h3 className="text-xl font-semibold mb-3 group-hover:text-primary-600 transition-colors">
                  {feature.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-400">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Testimonials Section */}
      <motion.section 
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
        className="py-20 bg-white dark:bg-gray-900"
      >
        <div className="container-custom">
          <motion.div 
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">What Our Clients Say</h2>
            <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              Don't just take our word for it - hear from our satisfied clients about their experience.
            </p>
          </motion.div>

          <motion.div 
            key={currentTestimonial}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.5 }}
            className="max-w-4xl mx-auto"
          >
            <div className="bg-gradient-to-r from-primary-50 to-secondary-50 dark:from-gray-800 dark:to-gray-700 rounded-2xl p-8 text-center">
              <div className="flex justify-center mb-4">
                {[...Array(testimonials[currentTestimonial].rating)].map((_, i) => (
                  <Star key={i} className="w-6 h-6 text-yellow-400 fill-current" />
                ))}
              </div>
              <blockquote className="text-xl md:text-2xl text-gray-700 dark:text-gray-300 mb-6 italic">
                "{testimonials[currentTestimonial].text}"
              </blockquote>
              <div className="flex items-center justify-center">
                <img 
                  src={testimonials[currentTestimonial].image}
                  alt={testimonials[currentTestimonial].name}
                  className="w-12 h-12 rounded-full mr-4"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = '/img/avatar-placeholder.svg'; // fallback to avatar placeholder
                  }}
                />
                <div>
                  <p className="font-semibold text-gray-900 dark:text-white">
                    {testimonials[currentTestimonial].name}
                  </p>
                  <p className="text-gray-600 dark:text-gray-400 text-sm">Verified Client</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Testimonial indicators */}
          <div className="flex justify-center mt-8 space-x-2">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentTestimonial(index)}
                className={`w-3 h-3 rounded-full transition-colors ${
                  index === currentTestimonial ? 'bg-primary-600' : 'bg-gray-300'
                }`}
              />
            ))}
          </div>
        </div>
      </motion.section>



      {/* Contact Section */}
      <motion.section 
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
        className="py-20 bg-white dark:bg-gray-900"
      >
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ x: -50, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Visit Our Salon</h2>
              <p className="text-gray-600 dark:text-gray-400 mb-8">
                We're conveniently located in the heart of Plymouth. Drop by for a consultation or book your appointment today.
              </p>
              
              <div className="space-y-6">
                <motion.div 
                  whileHover={{ x: 5 }}
                  className="flex items-start group"
                >
                  <MapPin className="w-6 h-6 text-primary-600 mr-4 mt-1 group-hover:scale-110 transition-transform" />
                  <div>
                    <h3 className="font-semibold mb-1">Address</h3>
                    <p className="text-gray-600 dark:text-gray-400">Renown street, PL2 2DF</p>
                  </div>
                </motion.div>
                
                <motion.div 
                  whileHover={{ x: 5 }}
                  className="flex items-start group"
                >
                  <Clock className="w-6 h-6 text-primary-600 mr-4 mt-1 group-hover:scale-110 transition-transform" />
                  <div>
                    <h3 className="font-semibold mb-1">Opening Hours</h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Monday - Friday: 10:00 AM - 7:00 PM<br />
                      Saturday: 9:00 AM - 6:00 PM<br />
                      Sunday: Closed
                    </p>
                  </div>
                </motion.div>
                
                <motion.div 
                  whileHover={{ x: 5 }}
                  className="flex items-start group"
                >
                  <Phone className="w-6 h-6 text-primary-600 mr-4 mt-1 group-hover:scale-110 transition-transform" />
                  <div>
                    <h3 className="font-semibold mb-1">Contact</h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Phone: 07707467065<br />
                      Email: damilolar74@gmail.com
                    </p>
                  </div>
                </motion.div>
              </div>
            </motion.div>

            <motion.div 
              initial={{ x: 50, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="relative overflow-hidden rounded-2xl shadow-2xl">
                <img 
                  src="/img/3.jpg"
                  alt="Our Salon"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = '/img/placeholder.svg';
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
              </div>
              <motion.div 
                initial={{ scale: 0.8, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.3, duration: 0.5 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.05 }}
                className="absolute -bottom-6 -right-6 bg-gradient-to-r from-primary-600 to-secondary-600 text-white p-6 rounded-2xl shadow-xl cursor-pointer"
              >
                <Scissors className="w-8 h-8 mb-2" />
                <p className="text-lg font-semibold">Ready to transform your look?</p>
                <Link to="/booking" className="inline-flex items-center mt-2 text-white hover:underline group">
                  Book Now
                  <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </motion.section>
    </div>
  );
};

export default HomePage;